// Channel layer (ADR-0022, docs/21): node ↔ cloud inventory delegation, cloud → node booking commands, the
// public Channel API (/channels/v1) and the node's own request/reclaim endpoints.
import { z } from 'zod';
import { dateSchema, moneySchema, uuidSchema } from './common.js';
const amount4 = moneySchema.shape.amount;
const iso4217 = z.string().regex(/^[A-Z]{3}$/);
const count = z.number().int().min(0).max(100_000);
// ---------------------------------------------------------------- node → cloud: inventory push
export const inventoryCellSchema = z.object({
    roomTypeId: uuidSchema,
    night: dateSchema,
    /** free rooms on the node (physical − OOO − OOS − sold, cloud reservations already applied included) */
    free: count,
    physical: count,
    /** cloud sales the node has received as commands since the last ack (its view of `pending`) */
    pendingKnown: count,
    /** rule 2: how many more units the cloud may sell, from the node's policy */
    desiredRemaining: count,
    price: z.object({ amount: amount4, currency: iso4217 }).nullable(),
    version: z.number().int().min(0),
});
export const inventoryRoomTypeSchema = z.object({
    id: uuidSchema,
    code: z.string().min(1).max(16),
    nameAr: z.string().min(1).max(120),
    nameEn: z.string().max(120).nullable(),
    maxAdults: z.number().int().min(1).max(20),
    maxChildren: z.number().int().min(0).max(20),
    physical: count,
});
export const channelPolicySchema = z.object({
    /** D-05: same-day instant bookings are accepted until this hotel-local hour */
    instantSameDayUntilHour: z.number().int().min(0).max(23).default(18),
    /** D-04 */
    requestedTtlMinutes: z.number().int().min(15).max(1440).default(60),
    contactPhone: z.string().trim().max(40).nullable().default(null),
    timezone: z.string().min(1).max(64),
});
export const inventoryPushSchema = z.object({
    mode: z.enum(['delta', 'snapshot']),
    windowFrom: dateSchema,
    windowTo: dateSchema,
    currency: iso4217,
    policy: channelPolicySchema,
    roomTypes: z.array(inventoryRoomTypeSchema).max(100),
    cells: z.array(inventoryCellSchema).max(20_000),
});
export const inventoryPushAckSchema = z.object({
    lease: z.object({ issuedAt: z.iso.datetime(), validUntil: z.iso.datetime() }),
    /** effective budget per cell after rule 2, plus the cloud's pending count */
    cells: z.array(z.object({
        roomTypeId: uuidSchema,
        night: dateSchema,
        remaining: count,
        pending: count,
        version: z.number().int().min(0),
    })),
    /** the node pulls commands up to here before it applies the ack (rule 3) */
    commandWatermark: z.number().int().min(0),
});
// ---------------------------------------------------------------- cloud → node commands
export const channelGuestSchema = z.object({
    name: z.string().trim().min(2).max(160),
    phone: z.string().trim().min(5).max(40),
    email: z.string().trim().email().max(120).optional(),
    notes: z.string().trim().max(500).optional(),
});
export const channelBookingCommandSchema = z.object({
    cloudBookingId: uuidSchema,
    cloudConfirmation: z.string().min(4).max(32),
    channelCode: z.string().min(1).max(32),
    channelRef: z.string().max(80).nullable(),
    mode: z.enum(['instant', 'requested']),
    roomTypeId: uuidSchema,
    arrival: dateSchema,
    departure: dateSchema,
    adults: z.number().int().min(1).max(10),
    children: z.number().int().min(0).max(10),
    guest: channelGuestSchema,
    nightlyPrices: z
        .array(z.object({ date: dateSchema, amount: amount4, currency: iso4217 }))
        .max(366)
        .nullable(),
    requestedUntil: z.iso.datetime().nullable(),
});
export const channelCancelCommandSchema = z.object({
    cloudBookingId: uuidSchema,
    reason: z.string().trim().min(2).max(300),
});
export const channelDecideCommandSchema = z.object({
    cloudBookingId: uuidSchema,
    decision: z.enum(['accept', 'reject']),
    reason: z.string().trim().max(300).optional(),
});
// ---------------------------------------------------------------- node API (desk)
export const channelRequestDecisionSchema = z.object({
    decision: z.enum(['accept', 'reject']),
    reason: z.string().trim().max(300).optional(),
});
export const reclaimSchema = z
    .object({
    roomTypeId: uuidSchema,
    from: dateSchema,
    to: dateSchema,
    minutes: z.number().int().min(5).max(120).default(15),
})
    .refine((v) => v.to > v.from, 'to must be after from');
/** property.settings.channels (BR: per property) */
export const channelSettingsSchema = z.object({
    enabled: z.boolean().default(false),
    ratePlanId: uuidSchema.nullable().default(null),
    keepLocalWhenFreeAtMost: z.number().int().min(0).max(20).default(3),
    maxSharePct: z.number().int().min(0).max(100).default(50),
    instantSameDayUntilHour: z.number().int().min(0).max(23).default(18),
    requestedTtlMinutes: z.number().int().min(15).max(1440).default(60),
    contactPhone: z.string().trim().max(40).nullable().default(null),
    /** days ahead published to the channels */
    horizonDays: z.number().int().min(7).max(365).default(180),
});
// ---------------------------------------------------------------- public Channel API (/channels/v1)
export const channelHoldSchema = z
    .object({
    roomTypeId: uuidSchema,
    arrival: dateSchema,
    departure: dateSchema,
    adults: z.number().int().min(1).max(10).default(1),
    children: z.number().int().min(0).max(10).default(0),
    guest: channelGuestSchema,
    /** the channel's own reference (PNR); echoed back to the hotel */
    channelRef: z.string().trim().max(80).optional(),
})
    .refine((v) => v.departure > v.arrival, 'departure must be after arrival');
export const channelCancelSchema = z.object({ reason: z.string().trim().max(300).optional() });
// ---------------------------------------------------------------- cloud consoles
export const channelInputSchema = z.object({
    code: z
        .string()
        .trim()
        .regex(/^[a-z0-9_-]{2,32}$/),
    nameAr: z.string().trim().min(2).max(120),
    nameEn: z.string().trim().max(120).optional(),
    rateLimitPerMinute: z.number().int().min(10).max(10_000).default(120),
});
export const propertyChannelSettingsSchema = z.object({
    publicSlug: z
        .string()
        .trim()
        .regex(/^[a-z0-9-]{3,48}$/)
        .nullable()
        .optional(),
    channelsEnabled: z.boolean().optional(),
});
//# sourceMappingURL=channel.js.map